import { useMemo } from 'react';
import { portalDs } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import type { ActivityInsightTransition } from '../types/usage-insights.types';
import { formatEventLabel, normaliseTransition } from '../utils/usage-insights.utils';

interface UsageInsightsTransitionGraphProps {
  transitions: ActivityInsightTransition[];
}

interface LayoutNode {
  id: string;
  label: string;
  x: number;
  y: number;
}

interface LayoutEdge {
  from: string;
  to: string;
  count: number;
  isSelfLoop: boolean;
}

const GRAPH_WIDTH = 720;
const GRAPH_HEIGHT = 420;
const NODE_RADIUS = 34;

export function UsageInsightsTransitionGraph({ transitions }: UsageInsightsTransitionGraphProps) {
  const { nodes, edges } = useMemo(() => buildLayout(transitions), [transitions]);
  const maxCount = Math.max(...edges.map((edge) => edge.count), 1);

  if (nodes.length === 0) {
    return (
      <section className="rounded-xl border border-[#EDEEF2] bg-white p-5">
        <h2 className={portalDs.sectionTitle}>State transitions</h2>
        <p className={cn(portalDs.caption, 'mt-2')}>No transition data for the selected period.</p>
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-[#EDEEF2] bg-white p-5">
      <h2 className={portalDs.sectionTitle}>State transitions</h2>
      <p className={cn(portalDs.caption, 'mt-2')}>
        Edge thickness reflects transition frequency. Self-loops are highlighted.
      </p>
      <div className="mt-4 overflow-x-auto">
        <svg
          viewBox={`0 0 ${GRAPH_WIDTH} ${GRAPH_HEIGHT}`}
          className="min-w-[720px] w-full"
          role="img"
          aria-label="State transition graph"
        >
          <defs>
            <marker
              id="arrowhead"
              markerWidth="8"
              markerHeight="8"
              refX="7"
              refY="4"
              orient="auto"
            >
              <path d="M0,0 L8,4 L0,8 Z" fill="#64748B" />
            </marker>
            <marker
              id="arrowhead-self"
              markerWidth="8"
              markerHeight="8"
              refX="7"
              refY="4"
              orient="auto"
            >
              <path d="M0,0 L8,4 L0,8 Z" fill="#EA580C" />
            </marker>
          </defs>

          {edges.map((edge) => {
            const fromNode = nodes.find((node) => node.id === edge.from);
            const toNode = nodes.find((node) => node.id === edge.to);
            if (!fromNode || !toNode) {
              return null;
            }

            const strokeWidth = 1.5 + (edge.count / maxCount) * 8;
            if (edge.isSelfLoop) {
              return (
                <SelfLoopEdge
                  key={`${edge.from}-${edge.to}-${edge.count}`}
                  node={fromNode}
                  count={edge.count}
                  strokeWidth={strokeWidth}
                />
              );
            }

            return (
              <DirectedEdge
                key={`${edge.from}-${edge.to}-${edge.count}`}
                from={fromNode}
                to={toNode}
                count={edge.count}
                strokeWidth={strokeWidth}
              />
            );
          })}

          {nodes.map((node) => (
            <g key={node.id}>
              <circle
                cx={node.x}
                cy={node.y}
                r={NODE_RADIUS}
                fill="#FFFFFF"
                stroke="#2563EB"
                strokeWidth="2"
              />
              <text
                x={node.x}
                y={node.y}
                textAnchor="middle"
                dominantBaseline="middle"
                className="fill-[#0D0F1A] text-[10px] font-semibold"
              >
                {wrapLabel(node.label)}
              </text>
            </g>
          ))}
        </svg>
      </div>
    </section>
  );
}

function DirectedEdge({
  from,
  to,
  count,
  strokeWidth,
}: {
  from: LayoutNode;
  to: LayoutNode;
  count: number;
  strokeWidth: number;
}) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const distance = Math.hypot(dx, dy) || 1;
  const unitX = dx / distance;
  const unitY = dy / distance;
  const startX = from.x + unitX * (NODE_RADIUS + 4);
  const startY = from.y + unitY * (NODE_RADIUS + 4);
  const endX = to.x - unitX * (NODE_RADIUS + 10);
  const endY = to.y - unitY * (NODE_RADIUS + 10);
  const midX = (startX + endX) / 2;
  const midY = (startY + endY) / 2;

  return (
    <g>
      <line
        x1={startX}
        y1={startY}
        x2={endX}
        y2={endY}
        stroke="#64748B"
        strokeWidth={strokeWidth}
        markerEnd="url(#arrowhead)"
      />
      <text
        x={midX}
        y={midY - 6}
        textAnchor="middle"
        className="fill-[#5E6278] text-[10px] font-medium"
      >
        {count}
      </text>
    </g>
  );
}

function SelfLoopEdge({
  node,
  count,
  strokeWidth,
}: {
  node: LayoutNode;
  count: number;
  strokeWidth: number;
}) {
  const loopX = node.x + NODE_RADIUS + 8;
  const loopY = node.y - NODE_RADIUS - 8;

  return (
    <g>
      <path
        d={`M ${node.x + 12} ${node.y - 20}
           C ${loopX + 36} ${loopY - 10}, ${loopX + 36} ${loopY + 50}, ${node.x + 4} ${node.y + 24}`}
        fill="none"
        stroke="#EA580C"
        strokeWidth={Math.max(strokeWidth, 3)}
        strokeDasharray="6 4"
        markerEnd="url(#arrowhead-self)"
      />
      <text
        x={loopX + 18}
        y={loopY + 18}
        textAnchor="middle"
        className="fill-[#EA580C] text-[11px] font-bold"
      >
        {count}
      </text>
    </g>
  );
}

function wrapLabel(label: string): string {
  const words = label.split(' ');
  if (words.length <= 2) {
    return label;
  }
  const midpoint = Math.ceil(words.length / 2);
  return `${words.slice(0, midpoint).join(' ')}\n${words.slice(midpoint).join(' ')}`;
}

function buildLayout(transitions: ActivityInsightTransition[]): {
  nodes: LayoutNode[];
  edges: LayoutEdge[];
} {
  const normalised = transitions.map(normaliseTransition).filter(
    (transition) => transition.from_event && transition.to_event,
  );
  const eventIds = Array.from(
    new Set(normalised.flatMap((transition) => [transition.from_event, transition.to_event])),
  );

  const centreX = GRAPH_WIDTH / 2;
  const centreY = GRAPH_HEIGHT / 2;
  const radius = Math.min(GRAPH_WIDTH, GRAPH_HEIGHT) * 0.34;

  const nodes: LayoutNode[] = eventIds.map((id, index) => {
    const angle = (index / eventIds.length) * Math.PI * 2 - Math.PI / 2;
    return {
      id,
      label: formatEventLabel(id),
      x: centreX + Math.cos(angle) * radius,
      y: centreY + Math.sin(angle) * radius,
    };
  });

  const edges: LayoutEdge[] = normalised.map((transition) => ({
    from: transition.from_event,
    to: transition.to_event,
    count: transition.count,
    isSelfLoop: transition.from_event === transition.to_event,
  }));

  return { nodes, edges };
}
